import { forwardRef } from 'react';

import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { format, isSameDay } from 'date-fns';
import { ko } from 'date-fns/locale';

import { datePickerFloating } from './DatePicker.css';
import { SingleDatePickerPanel } from './SingleDatePickerPanel';
import { SingleDatePickerProps } from './types';
import { useDatePickerTrigger } from './useDatePickerTrigger';
import { Button, IconButton } from '../Button';
import { IconCalendarLine, IconXLine } from '../Icon';

export const SingleDatePicker = forwardRef<HTMLButtonElement, SingleDatePickerProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      placeholder,
      minDate,
      maxDate,
      buttonProps,
      className,
      disabled = false,
      closable = false,
      limitHeightToViewport = false,
    } = props;

    const {
      isOpen,
      refs,
      floatingStyles,
      context,
      getReferenceProps,
      getFloatingProps,
      handleClose,
    } = useDatePickerTrigger({ limitHeightToViewport });

    const formatDisplayValue = () => {
      if (!value) return placeholder || '날짜 선택';
      const label = format(value, 'yy-MM-dd', { locale: ko });

      if (isSameDay(value, new Date())) {
        return `오늘 (${label})`;
      }

      return label;
    };

    return (
      <>
        <Button
          ref={(node) => {
            refs.setReference(node);
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          className={className}
          disabled={disabled}
          prefix={<IconCalendarLine width={18} height={18} />}
          closable={closable}
          suffix={
            closable ? (
              <IconButton
                buttonType="ghost"
                buttonSize="s"
                icon={<IconXLine />}
                tooltipMessage="초기화"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(undefined);
                  handleClose();
                }}
              />
            ) : undefined
          }
          {...buttonProps}
          {...getReferenceProps()}
        >
          {formatDisplayValue()}
        </Button>

        {isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal>
              <div
                ref={refs.setFloating}
                className={datePickerFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <SingleDatePickerPanel
                  value={value}
                  onChange={onChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  onClose={handleClose}
                />
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </>
    );
  },
);

SingleDatePicker.displayName = 'SingleDatePicker';
